/**
 * Utility function to load checklist data for a specific property
 * @param {string} propertyId - The ID of the property
 * @returns {Promise<Object>} - The checklist data for the property
 */
export const loadChecklistData = async (propertyId) => {
  try {
    // Import the JSON file dynamically
    const checklistData = await import(`../data/checklists/property-${propertyId}.json`);
    return checklistData.default;
  } catch (error) {
    console.error(`Failed to load checklist data for property ${propertyId}:`, error);
    throw new Error(`Checklist data not found for property ${propertyId}`);
  }
};

/**
 * Get localized text based on current language
 * @param {Object} textObject - Object containing translations (e.g., {en: 'text', es: 'texto', zh: '文本'})
 * @param {string} language - Current language code (en, es, zh)
 * @returns {string} - Localized text
 */
export const getLocalizedText = (textObject, language) => {
  if (!textObject || typeof textObject !== 'object') {
    return '';
  }
  
  // Return the text for the current language, fallback to English, then to first available
  return textObject[language] || textObject.en || Object.values(textObject)[0] || '';
};

/**
 * Transform checklist data to localized format
 * @param {Object} checklistData - Raw checklist data
 * @param {string} language - Current language code
 * @returns {Object} - Localized checklist data
 */
export const localizeChecklistData = (checklistData, language) => {
  if (!checklistData || !checklistData.categories) {
    return { categories: [] };
  }

  return {
    ...checklistData,
    categories: checklistData.categories.map(category => ({
      ...category,
      name: getLocalizedText(category.name, language),
      tasks: category.tasks.map(task => ({
        ...task,
        title: getLocalizedText(task.title, language),
        description: getLocalizedText(task.description, language)
      }))
    }))
  };
};